import type { ModuleConfig, NavSection, ValidatedModule } from './types'

/**
 * Canonical NavSection rank used by the registry sort. Order matches
 * `SECTION_ORDER` in Shell.tsx so the registry's flat order (consumed
 * by the command palette + breadcrumb) lines up with the visible
 * sidebar order.
 */
const SECTION_RANK: Readonly<Record<NavSection, number>> = {
  home: 0,
  executive: 1,
  sales: 2,
  customer_service: 3,
  purchasing: 4,
  regulatory: 5,
  warehouse: 6,
  logistics: 7,
  formulations: 8,
  finance: 9,
  admin: 10,
  vendors: 11,
  hidden: 12,
}

const VALID_NAV_SECTIONS = new Set<NavSection>([
  'home',
  'executive',
  'sales',
  'customer_service',
  'purchasing',
  'regulatory',
  'warehouse',
  'logistics',
  'formulations',
  'finance',
  'admin',
  'vendors',
  'hidden',
])

// Discover every module.config.ts under src/modules/* at build time.
// Vite's `import.meta.glob` with `eager: true` means this is a compile-
// time tree-shakable scan — no runtime directory walk.
//
// Adding a module = create a folder with module.config.ts.
// Deleting a module = delete the folder + rebuild.
const manifestImports = import.meta.glob<ModuleConfig>(
  '/src/modules/*/module.config.ts',
  { eager: true, import: 'default' },
) as Record<string, ModuleConfig>

const KEBAB = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/

function kebabToSlash(id: string): string {
  // `open-orders` → `/orders`? No — spec §5.1 URL namespace table says
  // `open-orders` → `/orders`. That mapping isn't derivable from the id
  // alone. Most modules' namespace equals their id; the exceptions (like
  // open-orders → /orders) use `urlNamespace` explicitly. Default here
  // is id-as-namespace.
  return `/${id}`
}

export class ManifestValidationError extends Error {
  readonly filePath?: string
  constructor(message: string, filePath?: string) {
    super(filePath ? `${filePath}: ${message}` : message)
    this.name = 'ManifestValidationError'
    this.filePath = filePath
  }
}

function validateOne(filePath: string, config: ModuleConfig): ValidatedModule {
  const require = (cond: unknown, msg: string) => {
    if (!cond) throw new ManifestValidationError(msg, filePath)
  }

  require(config && typeof config === 'object', 'manifest must be an object')
  require(typeof config.id === 'string' && config.id.length > 0, 'missing `id`')
  require(KEBAB.test(config.id), `id "${config.id}" must be kebab-case`)
  require(typeof config.name === 'string' && config.name.length > 0, 'missing `name`')
  require(typeof config.icon === 'function' || typeof config.icon === 'object', 'missing `icon`')
  require(
    typeof config.navSection === 'string' &&
      VALID_NAV_SECTIONS.has(config.navSection as NavSection),
    `navSection "${String(config.navSection)}" is not one of the valid section keys`,
  )
  require(typeof config.navOrder === 'number', 'missing `navOrder`')
  require(Array.isArray(config.routes) && config.routes.length > 0, 'routes must be a non-empty array')
  require(Array.isArray(config.permissions), 'permissions must be an array')
  require(
    config.densityDefault === 'compact' ||
      config.densityDefault === 'comfortable' ||
      config.densityDefault === 'spacious',
    'invalid densityDefault',
  )
  require(
    config.mobileStrategy === 'desktop-only' ||
      config.mobileStrategy === 'responsive' ||
      config.mobileStrategy === 'mobile-first',
    'invalid mobileStrategy',
  )

  // Check for duplicate paths within the module
  const seen = new Set<string>()
  for (const route of config.routes) {
    require(typeof route.path === 'string' && route.path.length > 0, 'route missing path')
    require(typeof route.element === 'function' || typeof route.element === 'object', 'route missing element')
    require(!seen.has(route.path), `duplicate route path "${route.path}"`)
    seen.add(route.path)
  }

  // Folder name must match id (catches rename drift)
  const folderMatch = filePath.match(/\/src\/modules\/([^/]+)\//)
  const folderName = folderMatch?.[1]
  require(
    folderName === config.id,
    `folder name "${folderName}" does not match id "${config.id}"`,
  )

  const resolvedNamespace = config.urlNamespace ?? kebabToSlash(config.id)
  require(resolvedNamespace.startsWith('/'), 'urlNamespace must start with `/`')

  return { ...config, resolvedNamespace }
}

function buildRegistry(imports: Record<string, ModuleConfig>): ReadonlyArray<ValidatedModule> {
  const validated: ValidatedModule[] = []
  const idSet = new Set<string>()
  const shortcutSet = new Set<string>()
  const namespaceSet = new Set<string>()

  for (const [filePath, config] of Object.entries(imports)) {
    const v = validateOne(filePath, config)
    if (idSet.has(v.id)) {
      throw new ManifestValidationError(`duplicate module id "${v.id}"`, filePath)
    }
    idSet.add(v.id)
    if (v.keyboardShortcut) {
      if (shortcutSet.has(v.keyboardShortcut)) {
        throw new ManifestValidationError(
          `duplicate keyboard shortcut "${v.keyboardShortcut}"`,
          filePath,
        )
      }
      shortcutSet.add(v.keyboardShortcut)
    }
    if (namespaceSet.has(v.resolvedNamespace)) {
      throw new ManifestValidationError(
        `duplicate URL namespace "${v.resolvedNamespace}"`,
        filePath,
      )
    }
    namespaceSet.add(v.resolvedNamespace)
    validated.push(v)
  }

  return Object.freeze(
    validated.sort((a, b) => {
      // Group by section (10-section model + transition strings), then
      // by navOrder ascending within section.
      const sectionDiff = SECTION_RANK[a.navSection] - SECTION_RANK[b.navSection]
      if (sectionDiff !== 0) return sectionDiff
      return a.navOrder - b.navOrder
    }),
  )
}

/** The compiled, validated, frozen module registry. Consumed by the shell. */
export const moduleRegistry: ReadonlyArray<ValidatedModule> = buildRegistry(manifestImports)

export function getModuleById(id: string): ValidatedModule | undefined {
  return moduleRegistry.find((m) => m.id === id)
}

/** Internal — used by unit tests to assemble a registry from a fake import map. */
export function _buildRegistryForTest(
  imports: Record<string, ModuleConfig>,
): ReadonlyArray<ValidatedModule> {
  return buildRegistry(imports)
}
